import { useContext, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Slider,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Layers as LayersIcon,
  PanoramaFishEye,
  Park,
  WaterDrop,
  Pets,
  Plumbing,
  DeleteSweep,
  ArrowDownward,
  Water,
  Grass,
  Help,
  ScatterPlot,
} from '@mui/icons-material'
import { t } from 'i18next'
import i18next from 'i18next'
import { get } from 'es-toolkit/compat'
import { TEST_IDS } from '../../../test-ids'
import { FEATURE_FLAGS } from '../../../feature-flags'
import * as c from 'common/c'
import {
  setOutsideBoundaryPointsOpacity,
  toggleOutsideBoundaryPointsLayer,
} from 'actions/map'
import FeatureFlagsContext from 'context/feature-flags-context'
import type { AppState } from 'state/index.js'

const ICONS: Record<string, React.ElementType> = {
  PanoramaFishEye,
  Park,
  WaterDrop,
  Pets,
  Plumbing,
  DeleteSweep,
  ArrowDownward,
  Water,
  Grass,
  Help,
}

export default function LayersControl() {
  const dispatch = useDispatch()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const features = useContext(FeatureFlagsContext)
  const operatorsEnabled = get(
    features,
    `${FEATURE_FLAGS.operators}.isEnabled`,
    false,
  )
  const outsidePointsEnabled = get(
    features,
    `${FEATURE_FLAGS.outsideBoundaryPoints}.isEnabled`,
    false,
  )

  const markerTypes = useSelector((state: AppState) => state.marker.markerTypes)
  const layerVisible = useSelector(
    (state: AppState) => state.marker.layerVisible,
  )
  const visibleTypeIds = useSelector(
    (state: AppState) => state.marker.visibleTypeIds,
  )
  const isCreating = useSelector((state: AppState) => state.marker.isCreating)

  const outsidePointsVisible = useSelector(
    (state: AppState) => state.map.outsideBoundaryPointsVisible,
  )
  const outsidePointsOpacity = useSelector(
    (state: AppState) => state.map.outsideBoundaryPointsOpacity,
  )
  const selectedYear = useSelector((state: AppState) => state.field.year)
  const isCurrentYear = selectedYear === new Date().getFullYear()

  const open = Boolean(anchorEl)
  const id = open ? 'layers-control-popover' : undefined

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLayerToggle = () => {
    dispatch({ type: c.MARKER_LAYER_TOGGLE })
  }

  const handleAddMarker = () => {
    handleClose()
    dispatch({ type: c.MARKER_CREATE_MODE_TOGGLE })
  }

  const isTypeVisible = (typeId: string) => {
    return visibleTypeIds === null || visibleTypeIds.includes(typeId)
  }

  const handleTypeToggle = (typeId: string) => {
    dispatch({
      type: c.MARKER_TYPE_FILTER_TOGGLE,
      payload: typeId,
    })
  }

  const allSelected =
    markerTypes.length > 0 &&
    (visibleTypeIds === null || visibleTypeIds.length === markerTypes.length)
  const someSelected =
    markerTypes.length > 0 &&
    visibleTypeIds !== null &&
    visibleTypeIds.length > 0 &&
    !allSelected

  const handleSelectAllToggle = () => {
    dispatch({
      type: c.MARKER_TYPES_SET_ALL_VISIBLE,
      payload: !allSelected,
    })
  }

  const handleOutsidePointsToggle = () => {
    dispatch(toggleOutsideBoundaryPointsLayer())
  }

  const handleOutsidePointsOpacityChange = (
    event: Event,
    value: number | number[],
  ) => {
    dispatch(setOutsideBoundaryPointsOpacity(value as number))
  }

  // Neither section would render - don't show an empty popover button.
  if (!operatorsEnabled && !outsidePointsEnabled) {
    return null
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 'calc(2em + 1px + 1.14 * 1.375em)',
        left: '56px',
        zIndex: 1000,
      }}
    >
      <Tooltip title={t('map_layers')}>
        <IconButton
          onClick={handleOpen}
          aria-label={t('map_layers')}
          aria-describedby={id}
          data-testid={TEST_IDS.layersControlButton}
          sx={{
            // This floats alone over the map, not in a packed row of
            // adjacent IconButtons, so the app-wide real-padding touch
            // target (theme.js) isn't needed here to keep neighbors from
            // overlapping — and a 32px icon with no padding already clears
            // WCAG's 24px minimum target size on its own. Overriding back
            // to the original padding:0 keeps this control's circle sized
            // to its (deliberately prominent, 2rem) icon instead of
            // inheriting the theme's default touch-target padding.
            padding: 0,
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <LayersIcon
            color={isCreating ? 'primary' : 'action'}
            sx={{ fontSize: '2rem' }}
          />
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box
          data-testid={TEST_IDS.layersControlPopover}
          sx={{
            p: 2,
            minWidth: 220,
          }}
        >
          {operatorsEnabled && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                {t('markers')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={layerVisible}
                    onChange={handleLayerToggle}
                    size="small"
                  />
                }
                label={t('marker_layer')}
              />
              <Box
                sx={{
                  mt: 1,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={handleAddMarker}
                  data-testid={TEST_IDS.markerAddButton}
                >
                  {t('add_marker')}
                </Button>
              </Box>
              {markerTypes.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('marker_types')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-testid={TEST_IDS.markerSelectAllCheckbox}
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={handleSelectAllToggle}
                        size="small"
                      />
                    }
                    label={t('select_all')}
                    sx={{ display: 'flex', my: -1 }}
                  />
                  {[...markerTypes]
                    .sort((a, b) =>
                      t(a.name, { defaultValue: a.name }).localeCompare(
                        t(b.name, { defaultValue: b.name }),
                        i18next.language,
                        { sensitivity: 'base' },
                      ),
                    )
                    .map((mt) => {
                      const Icon = mt.iconName ? ICONS[mt.iconName] : undefined
                      return (
                        <FormControlLabel
                          key={mt.id}
                          control={
                            <Checkbox
                              checked={isTypeVisible(mt.id)}
                              onChange={() => handleTypeToggle(mt.id)}
                              size="small"
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {Icon && (
                                <Icon
                                  fontSize="small"
                                  sx={{ color: 'action.active' }}
                                />
                              )}
                              {t(mt.name, { defaultValue: mt.name })}
                            </Box>
                          }
                          sx={{ display: 'flex', my: -1 }}
                        />
                      )
                    })}
                </>
              )}
            </>
          )}
          {outsidePointsEnabled && (
            <>
              {operatorsEnabled && <Divider sx={{ my: 1 }} />}
              <Typography variant="subtitle2" gutterBottom>
                {t('field_data')}
              </Typography>
              <Tooltip
                title={
                  isCurrentYear
                    ? ''
                    : t('outside_boundary_points_current_year_only')
                }
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={outsidePointsVisible}
                      onChange={handleOutsidePointsToggle}
                      disabled={!isCurrentYear}
                      size="small"
                      data-testid={TEST_IDS.outsideBoundaryPointsToggle}
                    />
                  }
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <ScatterPlot
                        fontSize="small"
                        sx={{ color: 'action.active' }}
                      />
                      {t('outside_boundary_points')}
                    </Box>
                  }
                />
              </Tooltip>
              <Box sx={{ pl: 4, pr: 1 }}>
                <FormControlLabel
                  control={
                    <Slider
                      value={outsidePointsOpacity}
                      onChange={handleOutsidePointsOpacityChange}
                      disabled={!isCurrentYear || !outsidePointsVisible}
                      min={0.0}
                      max={1.0}
                      step={0.01}
                      size="small"
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) =>
                        `${Math.round(value * 100)}%`
                      }
                      sx={{ width: '100%', mr: 1 }}
                      data-testid={TEST_IDS.outsideBoundaryPointsOpacitySlider}
                    />
                  }
                  label={t('layer_opacity')}
                  labelPlacement="start"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    ml: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.8125rem',
                      color: 'text.secondary',
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  )
}
